import React, { useEffect, useRef } from 'react';
import { useField, useFormApi } from 'informed';

import { TextField } from '@nw/omega-design-system';
import { HelperTextStyledComponent } from '@nw/shared/ui';

import { allowedCharactersInTextFieldRegexPattern } from '../../constants/RegexPatternConstants';
import { OutlineInfoCircleIcon } from '../../icons/OutlineInfoCircleIcon';

import { FieldContainer, IconContainer } from './styledComponents';

export type InputModeTypes =
  | 'text'
  | 'email'
  | 'tel'
  | 'url'
  | 'numeric'
  | 'decimal';

interface InputFieldProps {
  field: string;
  isDisabled: boolean;
  label: string;
  fieldType: string;
  containerStyles?: any;
  inputContainerStyles?: any;
  labelStyles?: any;
  focusIcon?: any;
  shouldShowIcon: boolean;
  className?: string;
  validationSchemaObject: any;
  helperText?: string;
  touched: boolean;
  error: string;
  placeholder?: string;
  helperImageUrl?: string;
  isHidden?: boolean;
  fieldWrapperStyles?: any;
  fontColors?: any;
  helperComponent?: any;
  setSelectedSuggestedPaymentAmountIndex?: any;
  onChangeInputField?: any;
  inputFieldStyles?: any;
  mask?: any;
  maintainCursor?: any;
  parser?: any;
  formatter?: any;
  reRenderedFieldsObjects?: any;
  omegaFieldContainerStyles?: string | any;
  inputMode?: InputModeTypes;
  leftElement?: any;
  rightElement?: any;
  autoFocus?: boolean;
  handleBlur?: any;
  iconStyles?: any;
  autoComplete?: 'on' | 'off' | 'one-time-code';
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  value?: any;
  color?: string;
  leftElementContainerClassName?: string;
  isMarginBottomZero?: boolean;
  hintClassName?: string;
  inputClassName?: string;
  inputContainerClassName?: string;
  showErrorIcon?: boolean;
  maxLength?: number;
  pattern?: string;
  handleFocus?: () => void;
  onValueChange?: any;
  shouldShowLabel?: boolean;
  enableDataMask?: boolean;
  shouldShowErrorIcon?: boolean;
  dependentOnFields?: string[];
  values?: Record<string, any>;
  touchedFields?: any;
  shouldAllowSpecialCharacters?: boolean;
  rightElementContainerClassName?: string;
}

const InformedTextField = React.memo((props: InputFieldProps) => {
  const {
    label,
    fieldType,
    containerStyles,
    inputContainerStyles,
    labelStyles,
    focusIcon,
    isDisabled,
    shouldShowIcon,
    className,
    validationSchemaObject,
    field,
    helperText,
    touched,
    error,
    placeholder,
    helperImageUrl,
    isHidden,
    fieldWrapperStyles,
    autoComplete,
    fontColors,
    helperComponent,
    setSelectedSuggestedPaymentAmountIndex,
    onChangeInputField,
    inputFieldStyles,
    reRenderedFieldsObjects,
    omegaFieldContainerStyles,
    leftElement,
    rightElement,
    rightElementContainerClassName,
    autoFocus,
    handleBlur,
    iconStyles,
    validateOnBlur = true,
    validateOnChange = true,
    value: controlledValue,
    color,
    leftElementContainerClassName,
    isMarginBottomZero,
    hintClassName,
    inputClassName,
    inputContainerClassName,
    showErrorIcon = false,
    maxLength,
    pattern,
    handleFocus,
    onValueChange,
    shouldShowLabel = true,
    enableDataMask,
    shouldShowErrorIcon = false,
    dependentOnFields = [],
    values,
    touchedFields,
    shouldAllowSpecialCharacters = true,
    ...other
  } = props;

  const isInputFocusedRef = useRef(false);

  const { fieldState, render, informed, fieldApi } = useField({
    field,
    validateOnBlur: validateOnBlur,
    validateOnChange: validateOnChange,
    validationSchema: validationSchemaObject.fields[field],
    error: error,
    touched: touched,
    setSelectedSuggestedPaymentAmountIndex:
      setSelectedSuggestedPaymentAmountIndex,
    isDisabled,
    label,
    onValueChange: onValueChange,
    ...reRenderedFieldsObjects
  });

  const { value } = fieldState;
  const { onBlur, onChange } = informed;
  const { setTouched, setValue } = fieldApi;

  const isNotValid = touched && error ? true : false;

  const handleOnBlur = (e) => {
    isInputFocusedRef.current = false;
    onBlur(e);
    if (handleBlur) {
      handleBlur();
    }
  };

  const handleOnFocus = (): void => {
    isInputFocusedRef.current = true;
    handleFocus && handleFocus();
  };

  useEffect(() => {
    if (controlledValue !== undefined) setValue(controlledValue);
  }, [controlledValue]);

  const formApi = useFormApi();

  const hasMounted = useRef(false);
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    if (dependentOnFields.length > 0) {
      let isTouched = false;
      dependentOnFields.forEach((eachField: string) => {
        if (touchedFields[eachField]) isTouched = true;
      });
      if (isTouched) {
        formApi.setValue(field, '');
      }
    }
  }, [...dependentOnFields.map((field) => values?.[field])]);

  const onChangeField = (value: string) => {
    const updatedValue = shouldAllowSpecialCharacters
      ? value
      : value.replace(allowedCharactersInTextFieldRegexPattern, '');
    onChangeInputField && onChangeInputField(updatedValue);
    setTouched(true);
    onChange(updatedValue);
  };

  const renderLeftElement = () => {
    const FocusIcon = focusIcon;
    return FocusIcon ? (
      <FocusIcon
        status={isInputFocusedRef.current}
        isNotValid={isNotValid && !isInputFocusedRef.current}
      />
    ) : null;
  };

  const renderErrorIcon = () => {
    return isNotValid && !isInputFocusedRef.current && showErrorIcon ? (
      <IconContainer shouldShowLabel={shouldShowLabel}>
        <OutlineInfoCircleIcon />
      </IconContainer>
    ) : null;
  };

  const renderHelperText = () => {
    const HelperComponent = helperComponent;
    return helperText ? (
      <HelperTextStyledComponent helperText={helperText} />
    ) : helperComponent ? (
      <HelperComponent />
    ) : null;
  };

  const renderTextField = () => {
    const formattedValue =
      field === 'email' && value === 'dummy#@gmail.com' ? undefined : value;
    return (
      <TextField
        value={formattedValue as string}
        hint={renderHelperText()}
        errorMessage={error}
        inputMode={fieldType as InputModeTypes}
        type={fieldType as InputModeTypes}
        isDisabled={isDisabled}
        label={label}
        name={field}
        onChange={onChangeField}
        placeholder={placeholder}
        leftElement={
          shouldShowIcon
            ? leftElement
              ? leftElement()
              : renderLeftElement
            : null
        }
        rightElement={rightElement ? rightElement : null}
        autoFocus={autoFocus}
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
        containerClassName={omegaFieldContainerStyles}
        autoComplete={autoComplete}
        color={color}
        leftElementContainerClassName={leftElementContainerClassName}
        hintClassName={hintClassName}
        inputClassName={inputClassName}
        inputContainerClassName={inputContainerClassName}
        maxLength={maxLength}
        pattern={pattern}
        labelClassName={labelStyles}
        enableDataMask={enableDataMask}
        rightElementContainerClassName={rightElementContainerClassName}
        {...other}
      />
    );
  };

  return render(
    <FieldContainer
      key={field}
      isHidden={isHidden}
      css={fieldWrapperStyles}
      isMarginBottomZero={isMarginBottomZero}
    >
      {renderTextField()}
      {shouldShowErrorIcon && renderErrorIcon()}
    </FieldContainer>
  );
});

InformedTextField.displayName = 'InformedTextField';

export { InformedTextField };
